import type { ModalProps } from '~/util';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { useAppForm } from '~/util/hooks/useAppForm';
import { formOptions } from '@tanstack/react-form';
import { useApiMutation } from '~/util/hooks/useApiMutation';

import { getRouteApi } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { clanFormInput, clanFormSchema } from '~/util/forms/clan';
import { editClanStaffMutation } from '~/util/mutations/staff/clans';
import { deleteClanMutation } from '~/util/mutations/public/clans';
import { Button } from '~/primitives/button';
import { Trash } from 'lucide-react';
import ConfirmModal from '~/components/ConfirmModal';

const Route = getRouteApi('/profile/clan/$clanid');

export const EditClanSheet = (modalProps: ModalProps) => {
    const clan = Route.useLoaderData({
        select: (data) => data.profile.clan,
    });

    const { clanid } = Route.useParams();
    const navigate = Route.useNavigate();

    const [deleteClanOpen, setDeleteClanOpen] = useState(false);

    const editMutation = useApiMutation(editClanStaffMutation());
    const deleteMutation = useApiMutation({
        ...deleteClanMutation(),
        onSuccess: () => {
            navigate({ to: '/staff/clans' });
        },
    });

    const currentClan = useMemo(
        () => ({
            global_name: clan.global_name,
            description: clan.description,
            tag: clan.tag,
        }),
        [clan],
    );

    const clanForm = useAppForm(
        formOptions({
            formId: 'edit-clan-staff',
            defaultValues: currentClan as clanFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: clanFormSchema,
            },
            onSubmit: async ({ value }) => {
                editMutation.mutate({
                    clan: value,
                    existingClan: currentClan,
                    existingClanId: clanid,
                });
            },
        }),
    );

    useEffect(() => {
        clanForm.reset(currentClan);
    }, [clanForm, currentClan]);

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Edit Clan</SheetTitle>
                        <SheetDescription>
                            Update clan details or remove the clan.
                        </SheetDescription>
                    </SheetHeader>
                    <form
                        id="edit-clan-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            clanForm.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <clanForm.AppField name="global_name">
                                {(field) => (
                                    <field.TextField
                                        label="Name"
                                        required
                                        placeholder="Clan name"
                                    />
                                )}
                            </clanForm.AppField>
                            <clanForm.AppField name="description">
                                {(field) => (
                                    <field.TextAreaField
                                        classname="w-lg"
                                        label="Description"
                                        placeholder="Clan description"
                                    />
                                )}
                            </clanForm.AppField>
                            <clanForm.AppField name="tag">
                                {(field) => (
                                    <field.TextField
                                        label="Tag"
                                        required
                                        placeholder="Clan tag"
                                    />
                                )}
                            </clanForm.AppField>
                        </div>

                        <SheetFooter className="sm:justify-center">
                            <div className="w-full flex gap-2">
                                <clanForm.AppForm>
                                    <clanForm.SubscribeButton
                                        label="Edit clan"
                                        classname="w-2/3"
                                    />
                                </clanForm.AppForm>
                                <Button
                                    variant="destructive"
                                    type="button"
                                    onClick={() => setDeleteClanOpen(true)}
                                >
                                    <Trash /> Delete clan
                                </Button>
                            </div>
                        </SheetFooter>
                    </form>
                    <ConfirmModal
                        open={deleteClanOpen}
                        onOpenChange={setDeleteClanOpen}
                        title="Delete clan"
                        text={`Are you sure you want to delete the clan "${currentClan?.global_name}"? This action cannot be undone.`}
                        onConfirm={() => deleteMutation.mutate(clanid)}
                    />
                </SheetContent>
            </Sheet>
        </>
    );
};
